import { Box, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { Attachment } from 'modules/core/components/TetherComponent/types'

export interface Props {
  label: string | React.ReactNode
  targetAttachment?: Attachment
  attachment?: Attachment
  visible?: boolean
}

// This component prevents rendering the tooltip when it's not open to optimize performance and memory usage.
// It's particularly useful in the monthly view since the Tether library, which updates X and Y coordinates on scroll,
// can cause performance issues with thousands of elements.
const TooltipWrapper = ({
  label,
  children,
  targetAttachment,
  attachment = 'top center',
  visible = true,
}: React.PropsWithChildren<Props>) => {
  const [hover, toggleHover] = useToggle(false)
  const showTooltip = hover && visible

  const onMouseEnter = () => toggleHover(true)()
  const onMouseLeave = () => toggleHover(false)()

  return (
    <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid="tooltip-wrapper">
      {/* This style avoids multiple opened tooltips and onMouseLeave works properly. */}
      <Box style={{ pointerEvents: 'none' }}>
        {!showTooltip && children}
        {showTooltip && (
          <Tooltip.Alt
            attachment={attachment}
            targetAttachment={targetAttachment}
            open
            label={label}
          >
            {children}
          </Tooltip.Alt>
        )}
      </Box>
    </Box>
  )
}

const Alt = observer((props: React.PropsWithChildren<Props>) => <TooltipWrapper {...props} />)

const ItemList = observer(
  ({
    label,
    children,
    targetAttachment,
    attachment = 'top center',
    visible = true,
  }: React.PropsWithChildren<Props>) => {
    const [hover, toggleHover] = useToggle(false)
    const showTooltip = hover && visible

    const onMouseEnter = () => toggleHover(true)()
    const onMouseLeave = () => toggleHover(false)()

    return (
      <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid="tooltip-wrapper">
        {/* This style avoids multiple opened tooltips and onMouseLeave works properly. */}
        <Box style={{ pointerEvents: 'none' }}>
          {!showTooltip && children}
          {showTooltip && (
            <Tooltip.ItemList attachment={attachment} targetAttachment={targetAttachment} open>
              {children}
              {label}
            </Tooltip.ItemList>
          )}
        </Box>
      </Box>
    )
  }
)

TooltipWrapper.ItemList = ItemList
TooltipWrapper.Alt = Alt

export default observer(TooltipWrapper)
